import { apiClient } from './client';

// Types for Website API
export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  urgency: 'low' | 'normal' | 'high' | 'urgent';
}

export interface ContactFormResponse {
  success: boolean;
  message: string;
  ticket_id?: string;
}

export interface InterestRate {
  id: number;
  bank_name: string;
  bank_id: number;
  rate: number;
  min_amount: number;
  maturity_days: number;
  rate_type: 'fixed' | 'variable';
  last_update: string;
  features: string[];
  rating: number;
  estimated_return: number;
  is_active: boolean;
}

export interface RatesApiResponse {
  rates: InterestRate[];
  total: number;
  last_update: string;
  market_status: 'open' | 'closed';
}

export interface NewsItem {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  slug: string;
  featured_image?: string;
  published_at: string;
  category: string;
  tags: string[];
  author: string;
}

export interface BlogPost {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  slug: string;
  featured_image?: string;
  published_at: string;
  category: string;
  tags: string[];
  author: string;
  read_time: number;
}

// Website API service
export const websiteApi = {
  // Contact form
  contact: {
    submit: async (formData: ContactFormData): Promise<ContactFormResponse> => {
      const response = await apiClient.post('/api/v1/cms/contact', formData);
      return response.data;
    },

    // For admin - list contact messages
    list: async (params: {
      page?: number;
      page_size?: number;
      status?: string;
      urgency?: string;
    } = {}): Promise<{ messages: any[]; total: number }> => {
      const response = await apiClient.get('/api/v1/cms/contact', { params });
      return response.data;
    }
  },

  // Live rates
  rates: {
    list: async (params: {
      maturity_days?: number;
      min_amount?: number;
      bank_id?: number;
      rate_type?: string;
    } = {}): Promise<RatesApiResponse> => {
      const response = await apiClient.get('/api/v1/banks/rates', { params });
      return response.data;
    },

    getByBank: async (bankId: number): Promise<InterestRate[]> => {
      const response = await apiClient.get(`/api/v1/banks/${bankId}/rates`);
      return response.data.rates || response.data;
    },

    getLive: async (): Promise<RatesApiResponse> => {
      const response = await apiClient.get('/api/v1/banks/rates/live');
      return response.data;
    },

    // Rate calculator
    calculate: async (params: {
      amount: number;
      rate: number;
      maturity_days: number;
    }): Promise<{
      initial_amount: number;
      interest_rate: number;
      maturity_days: number;
      total_interest: number;
      final_amount: number;
      monthly_breakdown: Array<{
        month: number;
        interest: number;
        total: number;
      }>;
    }> => {
      const response = await apiClient.post('/api/v1/banks/rates/calculate', params);
      return response.data;
    }
  },

  // Content management
  content: {
    getPage: async (slug: string): Promise<any> => {
      const response = await apiClient.get(`/api/v1/cms/pages/${slug}`);
      return response.data;
    },

    getNews: async (params: {
      page?: number;
      page_size?: number;
      category?: string;
    } = {}): Promise<{ news: NewsItem[]; total: number }> => {
      const response = await apiClient.get('/api/v1/cms/news', { params });
      return response.data;
    },

    getBlog: async (params: {
      page?: number;
      page_size?: number;
      category?: string;
    } = {}): Promise<{ posts: BlogPost[]; total: number }> => {
      const response = await apiClient.get('/api/v1/cms/blog', { params });
      return response.data;
    },

    getSettings: async (): Promise<any> => {
      const response = await apiClient.get('/api/v1/cms/settings');
      return response.data;
    }
  },

  // Newsletter subscription
  newsletter: {
    subscribe: async (email: string): Promise<{ success: boolean; message: string }> => {
      const response = await apiClient.post('/api/v1/cms/newsletter', { email });
      return response.data;
    },

    unsubscribe: async (email: string, token: string): Promise<{ success: boolean; message: string }> => {
      const response = await apiClient.post('/api/v1/cms/newsletter/unsubscribe', { email, token });
      return response.data;
    }
  }
};

// Utility functions
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatPercentage = (rate: number): string => {
  return `%${rate.toFixed(2)}`;
};

export const calculateEstimatedReturn = (amount: number, rate: number, days: number): number => {
  return (amount * rate * days) / (365 * 100);
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getMaturityLabel = (days: number): string => {
  if (days <= 30) return '1 Ay';
  if (days <= 90) return '3 Ay';
  if (days <= 180) return '6 Ay';
  if (days <= 365) return '1 Yıl';
  return `${Math.round(days / 365)} Yıl`;
};

export const getUrgencyColor = (urgency: string): string => {
  switch (urgency) {
    case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
    case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'normal': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getRatingStars = (rating: number): string => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  
  return '★'.repeat(fullStars) + (hasHalfStar ? '☆' : '') + '☆'.repeat(emptyStars);
}; 